class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.hoverTimeout = null;
    this.openedByHover = false;
    this.isDesktop = window.matchMedia('(min-width: 990px)').matches;
    this.isAnimating = false;
    
    // Track if menu was opened by click (if toggle happens without hover, it's a click)
    this.mainDetailsToggle.addEventListener('toggle', () => {
      // If menu opens and we didn't just hover, it was a click
      if (this.mainDetailsToggle.hasAttribute('open') && !this.openedByHover) {
        this.openedByHover = false; // Keep it false for click-opened menus
      } else if (!this.mainDetailsToggle.hasAttribute('open')) {
        // Menu closed, reset flag
        this.openedByHover = false;
      }
    });
    
    // Add hover functionality for desktop only
    if (this.isDesktop) {
      this.setupHover();
    }
    
    // Listen for screen size changes
    window.matchMedia('(min-width: 990px)').addEventListener('change', (e) => {
      this.isDesktop = e.matches;
      if (this.isDesktop && !this.hoverSetup) {
        this.setupHover();
      }
    });
  }

  setupHover() {
    if (this.hoverSetup) return;
    this.hoverSetup = true;
    
    const summary = this.mainDetailsToggle.querySelector('summary');
    const content = this.mainDetailsToggle.querySelector('.mega-menu__content');
    
    // Open on hover (summary or content)
    const handleMouseEnter = () => {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }
      if (!this.mainDetailsToggle.hasAttribute('open')) {
        this.openedByHover = true;
        this.mainDetailsToggle.setAttribute('open', '');
        this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', 'true');
        if (this.header) {
          this.header.classList.add('header-wrapper--open');
        }
        this.onToggle();
      }
    };
    
    // Close on mouse leave with small delay to allow moving between summary and content
    const handleMouseLeave = () => {
      // Only close if opened by hover
      if (!this.openedByHover) return;
      
      this.hoverTimeout = setTimeout(() => {
        // Only close if still open and was opened by hover
        if (this.mainDetailsToggle.hasAttribute('open') && this.openedByHover) {
          this.close();
          this.openedByHover = false;
        }
      }, 200);
    };
    
    // Add event listeners to both summary and content
    summary.addEventListener('mouseenter', handleMouseEnter);
    if (content) {
      content.addEventListener('mouseenter', handleMouseEnter);
      content.addEventListener('mouseleave', handleMouseLeave);
    }
    // Also listen on the entire header-menu element to catch leaving from summary
    this.addEventListener('mouseleave', handleMouseLeave);
  }

  close() {
    super.close();
    if (this.header) {
      this.header.classList.remove('header-wrapper--open');
    }
  }

  animateHeight(isOpening) {
    if (this.isAnimating) return;
    this.isAnimating = true;
    
    const content = this.mainDetailsToggle.querySelector('.mega-menu__content');
    if (!content) {
      this.isAnimating = false;
      return;
    }

    if (isOpening) {
      // Opening: measure actual height and animate to it
      // Temporarily remove max-height to get full height
      const originalMaxHeight = content.style.maxHeight;
      content.style.maxHeight = 'none';
      const targetHeight = content.scrollHeight;
      content.style.maxHeight = originalMaxHeight;
      
      // Start from 0
      content.style.height = '0px';
      content.style.overflow = 'hidden';
      
      // Force reflow
      content.offsetHeight;
      
      // Animate to target height
      requestAnimationFrame(() => {
        content.style.transition = 'height 0.3s ease-in-out, opacity 0.3s ease-in-out';
        content.style.height = `${targetHeight}px`;
        content.style.opacity = '1';
        
        // After animation completes, set to auto and restore overflow
        setTimeout(() => {
          content.style.height = 'auto';
          content.style.overflow = 'auto';
          content.style.transition = '';
          this.isAnimating = false;
        }, 300);
      });
    } else {
      // Closing: animate from current height to 0
      const currentHeight = content.scrollHeight;
      content.style.height = `${currentHeight}px`;
      content.style.overflow = 'hidden';
      
      // Force reflow
      content.offsetHeight;
      
      // Animate to 0
      requestAnimationFrame(() => {
        content.style.transition = 'height 0.3s ease-in-out, opacity 0.3s ease-in-out';
        content.style.height = '0px';
        content.style.opacity = '0';
        
        // Reset styles after animation
        setTimeout(() => {
          content.style.height = '';
          content.style.overflow = '';
          content.style.transition = '';
          this.isAnimating = false;
        }, 300);
      });
    }
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    // Sync header-wrapper--open class with open state
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.header.classList.add('header-wrapper--open');
      // Animate height when opening
      this.animateHeight(true);
    } else {
      this.header.classList.remove('header-wrapper--open');
      // Animate height when closing
      this.animateHeight(false);
    }

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
